<template>
  <div
    v-if="publisherCampaign"
    class="text-sm pb-8"
  >
    <div class="md:pr-64 relative">
      <!-- Right Sidebar -->
      <div class="absolute hidden md:flex right-0 pl-8 w-64">
        <div class="flex flex-col gap-2 w-full">
          <!-- Caps Display -->
          <div v-if="publisherCampaign.caps && publisherCampaign.caps.length > 0" class="mb-4">
            <div class="uppercase text-xs font-medium mb-3 flex items-center">
              <div class="w-0.5 h-4 bg-primary rounded-full mr-2"></div>
              <span>Caps</span>
            </div>
            <div v-for="cap in publisherCampaign.caps" :key="cap.id" class="mb-2 text-xs">
              <span class="font-semibold">
                {{ cap.frequency.charAt(0).toUpperCase() + cap.frequency.slice(1) }} Cap:
              </span>
              <span class="italic">
                {{ cap.type != 'publisherActions' ? '$' : '' }}{{ cap.triggerValue }}
                {{ cap.type == 'publisherActions' ? 'actions' : '' }}
              </span>
            </div>
          </div>
          <div v-else-if="publisherCampaign.capDefaults && publisherCampaign.capDefaults.length > 0" class="mb-4">
            <div class="uppercase text-xs font-medium mb-3 flex items-center">
              <div class="w-0.5 h-4 bg-primary rounded-full mr-2"></div>
              <span>Caps</span>
            </div>
            <div v-for="cap in publisherCampaign.capDefaults" :key="cap.id" class="mb-2 text-xs">
              <span class="font-semibold">
                {{ cap.frequency.charAt(0).toUpperCase() + cap.frequency.slice(1) }} Cap:
              </span>
              <span class="italic">
                {{ cap.type != 'publisherActions' ? '$' : '' }}{{ cap.triggerValue }}
                {{ cap.type == 'publisherActions' ? 'actions' : '' }}
              </span>
            </div>
          </div>

          <!-- Allowed/Restricted Tags -->
          <div class="border-t border-base-content/10 pt-4 mt-2 flex flex-col gap-4">
            <div v-if="publisherCampaign.allowedRestrictedTags" class="flex flex-col gap-3 text-xs text-base-content/70 font-normal">
              <div v-if="publisherCampaign.allowedRestrictedTags.filter(tag => !tag.isNot).length > 0" class="flex flex-col gap-2 items-start">
                <div class="flex gap-1.5 items-center">
                  <v-icon
                    class="text-sky-500"
                    name="bi-check"
                    width="1.3em"
                    height="1.3em"
                  />
                  Allowed
                </div>
                <div class="flex items-center">
                  <div class="flex flex-wrap items-center gap-y-1 gap-x-1 text-xxs">
                    <Tag
                      v-for="tag of publisherCampaign.allowedRestrictedTags.filter(tag => !tag.isNot)"
                      :key="tag.name"
                      class="block bg-sky-200 !text-sky-500 border-sky-300 dark:bg-transparent dark:!text-accent border dark:border-primary/30"
                    >{{ tag.name }}</Tag>
                  </div>
                </div>
              </div>
              <div v-if="publisherCampaign.allowedRestrictedTags.filter(tag => tag.isNot).length > 0" class="flex flex-col gap-2 items-start">
                <div class="flex gap-1.5 items-center">
                  <v-icon
                    class="text-red-400 mx-[0.15rem]"
                    name="io-close"
                    width="0.95em"
                    height="0.95em"
                  />
                  Restricted
                </div>
                <div class="flex items-center">
                  <div class="flex flex-wrap items-center gap-y-2 gap-x-1 text-xxs">
                    <Tag
                      v-for="tag of publisherCampaign.allowedRestrictedTags.filter(tag => tag.isNot)"
                      :key="tag.name"
                      class="block bg-red-200 !text-red-400 border-red-400/25 dark:bg-transparent dark:!text-red-400 dark:border-red-400/30 border"
                    >{{ tag.name }}</Tag>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div>
        <h4 class="mb-2">
          <span class="pr-2 text-xl">{{ publisherCampaign.name }}</span>
          <sup class="text-base-content/30 text-sm">{{ publisherCampaign.id }}</sup>
        </h4>

        <div
          v-if="publisherCampaign.previewOptions.description"
          v-html="publisherCampaign.description"
          class="mt-4 mb-3 campaign-description text-sm"
        ></div>

        <div v-if="publisherCampaign.previewOptions.creativeUrl && publisherCampaign.redirect && publisherCampaign.redirect.status == 'subscribed'" class="mt-6">
          <a
            :href="publisherCampaign.creativeUrl"
            target="blank"
            class="text-primary underline hover:opacity-70 font-normal flex gap-1 items-center"
          >Creative Here
            <v-icon
              name="md-openinnew-round"
              height="1em"
              width="1em"
              scale="0.8"
            />
          </a>
        </div>
      </div>

      <div class="mt-8 py-2" v-if="publisherCampaign.redirect && publisherCampaign.redirect.status == 'subscribed'">
        <div class="mb-4">
          <XTable
            :data="visiblePayouts || []"
            class="w-full"
            :scroll-x="false"
          >
            <template v-slot:header-row>
              <th>Geo</th>
              <th>Payout</th>
            </template>
            <template v-slot:row="props">
              <td>
                <template v-if="props.row.countryCode == null">
                  All Countries
                </template>
                <template v-else>
                  <div class="flex gap-2 items-center">
                    {{ props.row.country.code }} -
                    {{ props.row.country.name }}
                  </div>
                </template>
              </td>
              <td>
                <template v-if="props.row.rate">
                  {{ props.row.rate }}
                </template>
                <template v-else-if="props.row.prettyType">
                  {{ props.row.prettyType }}
                </template>
              </td>
            </template>
          </XTable>
          <div v-if="collapseViewAvailable" class="py-1 mt-1">
            <template v-if="payoutsAreExpanded">
              <a class="link" @click.prevent="togglePayoutsAreExpanded"
                >Collapse</a
              >
            </template>
            <template v-else>
              <div class="flex items-center">
                <span class="text-base-content/50 mr-4"
                  >+ {{ invisibilePayoutsLength }} more geos</span
                >
                <a
                  class="link block"
                  @click.prevent="togglePayoutsAreExpanded"
                  >View All</a
                >
              </div>
            </template>
          </div>
        </div>

        <div class="mb-4">
          <div class="uppercase text-xs font-medium mb-3">Links</div>
          <div>
            <div
              v-if="
                publisherCampaign.offers && publisherCampaign.offers.length > 0
              "
            >
              <div
                class="flex items-center mb-3"
                v-for="offer in publisherCampaign.offers"
                :key="offer.id"
              >
                <div
                  v-if="offer.imageUrl || offer.imageThumbnailUrl || offer.legacyImageUrl || offer.legacyImageThumbnailUrl"
                  class="mr-3 flex-shrink-0"
                >
                  <img
                    :src="offer.imageThumbnailUrl || offer.legacyImageThumbnailUrl || offer.imageUrl || offer.legacyImageUrl"
                    :data-source="offer.imageUrl || offer.legacyImageUrl || offer.imageThumbnailUrl || offer.legacyImageThumbnailUrl"
                    class="w-12 h-12 rounded object-cover border border-base-200 dark:border-base-600 cursor-pointer hover:ring-2 ring-primary/50 transition-all"
                    :alt="offer.name"
                    v-viewer="{}"
                  />
                </div>
                <div
                  v-else
                  class="mr-3 flex-shrink-0 w-12 h-12 rounded bg-base-200 dark:bg-base-700 flex items-center justify-center"
                >
                  <v-icon
                    name="bi-image"
                    class="text-base-content/30"
                    width="1.5em"
                    height="1.5em"
                  />
                </div>

                <div class="flex-1 min-w-0">
                  <p class="mb-1 font-medium text-xs">
                    <template
                      v-if="
                        offer.destinationType == 'direct' ||
                        (offer.template && offer.template.path == 'redirect')
                      "
                    >
                      Direct
                    </template>
                    <template v-else-if="offer.template">
                      {{
                        offer.template.name
                          ? offer.template.name
                          : "Missing Name"
                      }}
                    </template>
                  </p>
                  <div
                    v-if="
                      publisherCampaign.redirect &&
                      publisherCampaign.redirect.status == 'subscribed'
                    "
                  >
                    <a
                      :href="`${publisherCampaign.redirect.url}/o/${offer.encodedId}${redirectParametersString}`"
                      class="text-primary underline hover:opacity-70 break-all text-xs"
                      target="_blank"
                      >{{ publisherCampaign.redirect.url }}/o/{{
                        offer.encodedId
                      }}{{ redirectParametersString }}</a
                    >
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else-if="
                publisherCampaign.redirect &&
                publisherCampaign.redirect.status == 'subscribed'
              "
              class="mb-2"
            >
              <div>
                <p class="mb-1 font-medium">Your Link</p>
                <a
                  class="text-primary underline hover:opacity-70 break-all"
                  :href="`${publisherCampaign.redirect.url}/${redirectParametersString}`"
                  target="_blank"
                  >{{ publisherCampaign.redirect.url }}/{{
                    redirectParametersString
                  }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <template v-else-if="publisherCampaign.redirect && publisherCampaign.redirect.status != 'subscribed'">
        <Alert
          :isShowing="true"
          alertMessage="User is not subscribed to this campaign"
          type="warning"
          class="mt-4"
        />
      </template>
    </div>
    <!-- Divider line -->
    <div class="border-b border-base-content/10 mt-8"></div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue"

const props = defineProps({
  publisherCampaign: Object,
  publisherOptions: Object,
})

const payoutsAreExpanded = ref(false)

const collapseViewAvailable = computed(() => {
  const incomingPayouts = props.publisherCampaign.payouts || []
  const previewOptions = props.publisherCampaign.previewOptions.payouts || []

  const allPayouts = incomingPayouts.filter((_, index) => previewOptions[index])

  const filteredPayouts = allPayouts.filter(
    payout => payout.rate !== "$0",
  )

  return filteredPayouts.length > 3
})

const visiblePayouts = computed(() => {
  const incomingPayouts = props.publisherCampaign.payouts || []
  const previewOptions = props.publisherCampaign.previewOptions.payouts || []

  const allPayouts = incomingPayouts.filter((_, index) => previewOptions[index])

  const removeZeroValuePayouts = allPayouts.filter(
    payout => payout.rate !== "$0",
  )

  let condensedPayouts = removeZeroValuePayouts.filter(
    (_, index) => props.publisherCampaign.previewOptions.payouts[index],
  )

  if (condensedPayouts.length < 3) {
    condensedPayouts = removeZeroValuePayouts.slice(0, 3)
  } else {
    condensedPayouts = condensedPayouts.slice(0, 3)
  }

  return payoutsAreExpanded.value ? removeZeroValuePayouts : condensedPayouts
})
const invisibilePayoutsLength = computed(() => {
  const removeZeroValuePayouts = props.publisherCampaign.payouts.filter(
    payout => payout.value !== 0,
  )

  return removeZeroValuePayouts.length - visiblePayouts.value.length
})

const redirectParametersString = computed(() => {
  let defaultParams = "?kw={source}&s1={clickid}"

  if (
    props.publisherOptions?.users?.data[0]?.userDetails
      .defaultRedirectParameters
  ) {
    let userParams = JSON.parse(
      props.publisherOptions.users.data[0].userDetails
        .defaultRedirectParameters,
    )

    let setUserParams = Object.entries(userParams)
      .filter(param => param[1])
      .map(param => `${param[0]}=${param[1]}`)
      .join("&")

    return `?${setUserParams}`
  }

  return defaultParams
})

const togglePayoutsAreExpanded = () => {
  payoutsAreExpanded.value = !payoutsAreExpanded.value
}
</script>